import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router";

import Table from "./Table";
import {
  selectActiveBrandsWithScoresState,
  selectBestCompetitorByWeeklyVisibilityState,
  selectSelectedCompetitorBrandState,
  selectSelectedBrandState,
} from "../../store/selectors/brands";
import { Brand } from "../../types/brand";
import { setViewBrandId } from "../../store/brands/slice";
import { setSelectedCompetitorBrandId } from "../../store/dailyPage/slice";
import TableSkeleton from "./TableSkeleton";
import DataLoadError from "../DataLoadError";
import { useScrollToHash, useAppDispatch, useAppSelector } from "../../hooks";
import { useSelectedBrands } from "./hooks/useSelectedBrands";
import { TableDailyConfig, ScoreDataByEntity } from "./types";

interface TableDailyProps<TEntityId = string | number> {
  entityId: TEntityId;
  index: number;
  config: TableDailyConfig<TEntityId>;
}

const TableDaily = <TEntityId extends string | number = string | number>({
  entityId,
  index,
  config,
}: TableDailyProps<TEntityId>) => {
  const { id, date } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  const brands = useAppSelector(selectActiveBrandsWithScoresState);
  const selectedBrand = useAppSelector(selectSelectedBrandState);
  const bestCompetitor = useAppSelector(selectBestCompetitorByWeeklyVisibilityState);
  const selectedCompetitorBrand = useAppSelector(selectSelectedCompetitorBrandState);
  
  const mainBrandId = selectedBrand?.id ?? null;

  const { resolvedCompetitor, selectedBrands, selectedBrandIds } = useSelectedBrands({
    brands,
    mainBrandId,
    bestCompetitor,
    selectedCompetitorBrand,
    selectedBrand,
  });

  const scoresData = useAppSelector(config.scoresSelector);
  const score = useMemo(
    () => (scoresData?.[String(entityId)] ?? null) as ScoreDataByEntity,
    [scoresData, entityId]
  );

  const baseRoute = useMemo(() => {
    if (!id || !date) return null;
    return `${config.baseRoute.replace(":id", String(id)).replace(":date", date)}#`;
  }, [date, id, config.baseRoute]);

  const columns = useMemo(() => config.buildColumns(selectedBrands), [config, selectedBrands]);

  const normalizedItems = useMemo(() => {
    if (!config.items?.length) return [];
    return config.normalizeItems ? config.normalizeItems(config.items) : config.items;
  }, [config, config.items]);

  const data = useMemo(() => {
    if (!baseRoute || !normalizedItems.length || !selectedBrands.length) return [];
    return config.buildData({
      items: normalizedItems,
      baseRoute,
      selectBrands: selectedBrands,
      score,
      isScoreLoading: !score,
    });
  }, [baseRoute, config, normalizedItems, score, selectedBrands]);

  useScrollToHash([score]);

  const previousBrandIdRef = useRef<string | undefined>(id);
  const previousSelectedBrandIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!id || !brands?.length) return;
    
    const brandId = Number(id);
    const hasBrandIdChanged = previousBrandIdRef.current !== id;
    const isSelectedBrandMismatch = selectedBrand?.id !== brandId;
    
    if (hasBrandIdChanged || isSelectedBrandMismatch) {
      const initialBrand = brands.find((b) => b.id === brandId) ?? brands[0];
      if (initialBrand && initialBrand.id !== selectedBrand?.id) {
        dispatch(setViewBrandId(initialBrand.id));
        previousSelectedBrandIdRef.current = initialBrand.id;
      }
    }
    
    previousBrandIdRef.current = id;
  }, [brands, dispatch, id, selectedBrand?.id]);

  useEffect(() => {
    if (!resolvedCompetitor) return;
    
    const shouldUpdateCompetitor = 
      !selectedCompetitorBrand || 
      selectedCompetitorBrand.id !== resolvedCompetitor.id;
    
    if (shouldUpdateCompetitor) {
      dispatch(setSelectedCompetitorBrandId(resolvedCompetitor.id));
    }
  }, [dispatch, resolvedCompetitor, selectedCompetitorBrand]);

  const previousBrandIdsRef = useRef<number[]>([]);
  const previousDateRef = useRef<string | undefined>(date);
  const previousEntityIdRef = useRef<string | number>(entityId);

  useEffect(() => {
    if (!date || !selectedBrandIds.length) return;

    const hasBrandIdsChanged = 
      previousBrandIdsRef.current.length !== selectedBrandIds.length ||
      previousBrandIdsRef.current.some((id, idx) => id !== selectedBrandIds[idx]);
    const hasDateChanged = previousDateRef.current !== date;
    const hasEntityChanged = previousEntityIdRef.current !== entityId;

    if (hasBrandIdsChanged || hasDateChanged || hasEntityChanged) {
      config.fetchScore({
        date,
        entityId,
        brandIds: selectedBrandIds,
      });
      config.fetchResponses({
        date,
        entityId,
        brandIds: selectedBrandIds,
      });

      previousBrandIdsRef.current = selectedBrandIds;
      previousDateRef.current = date;
      previousEntityIdRef.current = entityId;
    }
  }, [date, dispatch, entityId, selectedBrandIds, config]);

  const handleChangeBrand = useMemo(
    () => (columnId: string, newBrand: Brand) => {
      const match = columnId.match(/brand_group_(\d+)/);
      if (!match) return;

      const columnIndex = Number(match[1]);
      
      if (columnIndex === 0) {
        dispatch(setViewBrandId(newBrand.id));
        if (date) {
          const newPath = config.navigateRoute
            .replace(":id", String(newBrand.id))
            .replace(":date", date);
          navigate(newPath);
        }
      } else {
        dispatch(setSelectedCompetitorBrandId(newBrand.id));
      }
    },
    [date, dispatch, navigate, config.navigateRoute],
  );

  const hasNoData =
    !config.items?.length || !selectedBrands.length || !brands?.length;

  if (config.loading === true) {
    return (
      <TableSkeleton
        rows={config.skeletonRows}
        columns={3}
      />
    );
  }

  if (config.error) {
    return (
      <DataLoadError
        message={config.error}
        onRetry={config.onRetry}
      />
    );
  }

  if (hasNoData) {
    return (
      <TableSkeleton
        rows={config.skeletonRows}
        columns={3}
      />
    );
  }

  return (
    <Table
      key={index + String(entityId)}
      columns={columns}
      data={data}
      brands={brands}
      onChangeBrand={handleChangeBrand}
    />
  );
};

export default TableDaily;
